'use client';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { FC } from 'react';
import { DayPicker } from 'react-day-picker';

import { buttonVariants } from '@ui/components';
import { cn } from '@ui/lib/utils';
import dayjs from 'dayjs';

export type CalendarProps = Omit<
  React.ComponentProps<typeof DayPicker>,
  'selected' | 'mode'
> & {
  maxDate?: string;
  minDate?: string;
  weekendAllowed?: boolean;
  selected?: Date;
  mode?: 'single' | 'multiple' | 'range';
};

const Calendar: FC<CalendarProps> = ({
  className,
  classNames,
  showOutsideDays = false,
  selected,
  weekendAllowed,
  maxDate,
  minDate,
  ...props
}) => {
  const parsedMaxDate = maxDate ? dayjs(maxDate).toDate() : undefined;
  const parsedMinDate = minDate ? dayjs(minDate).toDate() : undefined;

  return (
    //@ts-ignore
    <DayPicker
      captionLayout="dropdown"
      className={cn('p-3 z-[10000000000]', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-black rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        day_disabled: 'text-gray opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="size-4" />,
        IconRight: () => <ChevronRight className="size-4" />,
      }}
      defaultMonth={
        dayjs(selected as Date).isValid()
          ? (selected as Date)
          : dayjs().toDate()
      }
      disabled={[
        {
          dayOfWeek: weekendAllowed ? [] : [0, 6],
        },
        {
          after: parsedMaxDate as never as Date,
          before: parsedMinDate as never as Date,
        },
      ]}
      mode="single"
      selected={selected}
      showOutsideDays={showOutsideDays}
      weekStartsOn={1}
      {...props}
    />
  );
};
Calendar.displayName = 'Calendar';

export { Calendar };
